/**
 * The five-step progress header for the Build Itinerary wizard. Presentational:
 * it renders the step circles, labels, and connector lines from the shared step
 * definitions + wizard state, and reports clicks on reachable steps.
 */

import {
  WIZARD_STEPS,
  canNavigateToStep,
  stepStatus,
  type WizardState,
  type WizardStepId,
} from './wizardSteps';
import './WizardStepper.css';

interface WizardStepperProps {
  state: WizardState;
  onStepSelect: (step: WizardStepId) => void;
}

export function WizardStepper({ state, onStepSelect }: WizardStepperProps) {
  return (
    <ol className="wiz-stepper" aria-label="Build itinerary progress">
      {WIZARD_STEPS.map((step, index) => {
        const status = stepStatus(step.id, state);
        const reachable = canNavigateToStep(step.id, state);
        const isLast = index === WIZARD_STEPS.length - 1;
        return (
          <li
            key={step.id}
            className={`wiz-step wiz-step--${status}`}
            aria-current={status === 'active' ? 'step' : undefined}
          >
            <button
              type="button"
              className="wiz-step__hit"
              disabled={!reachable}
              onClick={() => onStepSelect(step.id)}
              aria-label={`Step ${step.id}: ${step.label}. ${step.caption}.`}
              data-testid={`wizard-step-${step.id}`}
            >
              <span className="wiz-step__circle" aria-hidden="true">
                {status === 'done' ? '✓' : step.id}
              </span>
              {!isLast && <span className="wiz-step__line" aria-hidden="true" />}
              <span className="wiz-step__label">
                {step.label}
                <small className="wiz-step__caption">{step.caption}</small>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
